import React, { useState, useEffect, useRef } from 'react';
import AppLayout from '../components/layout/AppLayout';
import { getMe, getTicketStats, updateMyProfile, changeMyPassword, uploadFile } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import { X, Upload, Trash2 } from 'lucide-react';

const ROLE_COLOR = { admin: '#ef4444', consultant: '#f59e0b', user: '#3b82f6' };
const ROLE_LABEL = { admin: '🛡️ Admin', consultant: '💼 Consultant', user: '👤 User' };

export default function ProfilePage() {
  const { user: authUser, loginUser } = useAuth();
  const [profile,  setProfile]  = useState(null);
  const [stats,    setStats]    = useState(null);
  const [activeTab, setTab]     = useState('overview'); // 'overview' | 'edit' | 'security'
  const [loading,  setLoading]  = useState(true);

  /* edit profile state */
  const [editForm,    setEditForm]    = useState({ name: '', department: '', phone: '', avatar_url: '' });
  const avatarInputRef = useRef(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [editSaving,  setEditSaving]  = useState(false);
  const [editMsg,     setEditMsg]     = useState({ type: '', text: '' });

  /* change password state */
  const [pwdForm,    setPwdForm]    = useState({ current_password: '', new_password: '', confirm: '' });
  const [pwdSaving,  setPwdSaving]  = useState(false);
  const [pwdMsg,     setPwdMsg]     = useState({ type: '', text: '' });
  const [showPwd,    setShowPwd]    = useState({ current: false, new: false, confirm: false });

  useEffect(() => {
    Promise.all([getMe(), getTicketStats()])
      .then(([meRes, statsRes]) => {
        setProfile(meRes.data);
        setStats(statsRes.data);
        setEditForm({
          name:       meRes.data.name       || '',
          department: meRes.data.department || '',
          phone:      meRes.data.phone      || '',
          avatar_url: meRes.data.avatar_url || '',
        });
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) { setEditMsg({ type: 'error', text: 'Name is required.' }); return; }
    setEditSaving(true); setEditMsg({ type: '', text: '' });
    try {
      const res = await updateMyProfile(editForm);
      setProfile(p => ({ ...p, ...res.data }));
      // Update auth context name so topbar updates
      const stored = JSON.parse(sessionStorage.getItem('itsm_user') || '{}');
      const updated = { ...stored, name: res.data.name };
      sessionStorage.setItem('itsm_user', JSON.stringify(updated));
      setEditMsg({ type: 'success', text: 'Profile updated successfully!' });
    } catch (err) {
      setEditMsg({ type: 'error', text: err.response?.data?.error || 'Update failed.' });
    } finally { setEditSaving(false); }
  };

  const handleAvatarPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setAvatarUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await uploadFile(fd);
      // Persist immediately so it shows everywhere.
      const res = await updateMyProfile({ name: editForm.name || profile.name, avatar_url: data.url });
      setProfile(p => ({ ...p, ...res.data }));
      setEditForm(f => ({ ...f, avatar_url: data.url }));
      const stored = JSON.parse(sessionStorage.getItem('itsm_user') || '{}');
      sessionStorage.setItem('itsm_user', JSON.stringify({ ...stored, avatar_url: data.url }));
      setViewerOpen(false);
    } catch (err) {
      setEditMsg({ type: 'error', text: err.response?.data?.error || 'Image upload failed.' });
    } finally { setAvatarUploading(false); }
  };

  const handleAvatarDelete = async () => {
    setAvatarUploading(true);
    try {
      const res = await updateMyProfile({ name: editForm.name || profile.name, avatar_url: '' });
      setProfile(p => ({ ...p, ...res.data, avatar_url: '' }));
      setEditForm(f => ({ ...f, avatar_url: '' }));
      const stored = JSON.parse(sessionStorage.getItem('itsm_user') || '{}');
      sessionStorage.setItem('itsm_user', JSON.stringify({ ...stored, avatar_url: '' }));
      setViewerOpen(false);
    } catch (err) {
      setEditMsg({ type: 'error', text: err.response?.data?.error || 'Could not remove photo.' });
    } finally { setAvatarUploading(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwdForm.new_password !== pwdForm.confirm) {
      setPwdMsg({ type: 'error', text: 'New passwords do not match.' }); return;
    }
    if (pwdForm.new_password.length < 6) {
      setPwdMsg({ type: 'error', text: 'Password must be at least 6 characters.' }); return;
    }
    setPwdSaving(true); setPwdMsg({ type: '', text: '' });
    try {
      await changeMyPassword({ current_password: pwdForm.current_password, new_password: pwdForm.new_password });
      setPwdMsg({ type: 'success', text: 'Password changed successfully!' });
      setPwdForm({ current_password: '', new_password: '', confirm: '' });
    } catch (err) {
      setPwdMsg({ type: 'error', text: err.response?.data?.error || 'Password change failed.' });
    } finally { setPwdSaving(false); }
  };

  if (loading) {
    return (
      <AppLayout pageTitle="My Profile">
        <div className="flex-center" style={{ padding: 80 }}>
          <div className="spinner" style={{ width: 36, height: 36 }} />
        </div>
      </AppLayout>
    );
  }

  const initials = profile?.name?.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '??';
  const roleColor = ROLE_COLOR[profile?.role] || '#6b7280';

  return (
    <AppLayout pageTitle="My Profile">
      <div style={{ maxWidth: 860, margin: '0 auto' }}>

        {/* ══ Profile Hero Card ══ */}
        <div className="card mb-4" style={{ overflow: 'hidden' }}>
          {/* Gradient banner */}
          <div style={{
            height: 100,
            background: `linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)`,
            position: 'relative',
          }}>
            <div style={{
              position: 'absolute', inset: 0,
              backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.15) 0%, transparent 60%)',
            }} />
          </div>

          {/* Avatar + info */}
          <div style={{ padding: '0 28px 24px', position: 'relative' }}>
            {/* Avatar (click to view / change) */}
            <button type="button" onClick={() => profile?.avatar_url ? setViewerOpen(true) : avatarInputRef.current?.click()} title={profile?.avatar_url ? 'View photo' : 'Upload photo'}
              style={{
                width: 80, height: 80, borderRadius: '50%', padding: 0, cursor: 'pointer', overflow: 'hidden',
                background: profile?.avatar_url ? '#fff' : `linear-gradient(135deg, ${roleColor}, ${roleColor}cc)`,
                border: '4px solid var(--bg-card)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 28, fontWeight: 800, color: '#fff',
                marginTop: -40, position: 'relative', zIndex: 1,
                boxShadow: `0 4px 16px ${roleColor}55`,
              }}>
              {profile?.avatar_url
                ? <img src={profile.avatar_url} alt={profile?.name || 'avatar'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                : initials}
              <span style={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.5)', color: '#fff', fontSize: 8, fontWeight: 700, padding: '2px 0', letterSpacing: 0.3 }}>
                {avatarUploading ? '…' : '📷 EDIT'}
              </span>
            </button>
            <input ref={avatarInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleAvatarPick} />

            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginTop: 10 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: 'var(--text-primary)' }}>
                  {profile?.name}
                </h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{profile?.email}</span>
                  <span style={{
                    fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 20,
                    background: `${roleColor}18`, color: roleColor, border: `1px solid ${roleColor}33`,
                  }}>{ROLE_LABEL[profile?.role]}</span>
                  {profile?.department && (
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      🏢 {profile.department}
                    </span>
                  )}
                  {profile?.phone && (
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      📞 {profile.phone}
                    </span>
                  )}
                </div>
                {profile?.created_at && (
                  <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 6 }}>
                    Member since {format(new Date(profile.created_at), 'MMMM yyyy')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ══ Stats Row ══ */}
        {stats && (
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
            gap: 12, marginBottom: 20,
          }}>
            {[
              { label: 'Total Tickets', value: stats.total,        icon: '🎫', color: 'var(--primary)' },
              { label: 'Open',          value: stats.open_tickets,  icon: '📋', color: '#3b82f6' },
              { label: 'In Progress',   value: stats.in_progress,   icon: '⚙️', color: '#f59e0b' },
              { label: 'Resolved',      value: stats.resolved,      icon: '✅', color: '#10b981' },
              { label: 'Critical',      value: stats.critical_open, icon: '🔴', color: '#ef4444' },
            ].map(s => (
              <div key={s.label} className="card" style={{ padding: '16px 18px', textAlign: 'center' }}>
                <div style={{ fontSize: 20 }}>{s.icon}</div>
                <div style={{ fontSize: 26, fontWeight: 800, color: s.color, lineHeight: 1.1, marginTop: 4 }}>
                  {s.value ?? 0}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, marginTop: 3 }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ══ Tabs ══ */}
        <div className="card">
          {/* Tab bar */}
          <div style={{
            display: 'flex', borderBottom: '1px solid var(--border)',
            padding: '0 20px',
          }}>
            {[
              { key: 'edit',     icon: '✏️', label: 'Edit Profile' },
              { key: 'security', icon: '🔒', label: 'Change Password' },
            ].map(tab => (
              <button key={tab.key} onClick={() => setTab(tab.key)} style={{
                padding: '14px 18px', border: 'none', background: 'none', cursor: 'pointer',
                fontSize: 13.5, fontWeight: 600,
                color: activeTab === tab.key ? 'var(--primary)' : 'var(--text-muted)',
                borderBottom: activeTab === tab.key ? '2px solid var(--primary)' : '2px solid transparent',
                marginBottom: -1, display: 'flex', alignItems: 'center', gap: 6,
                transition: 'color 0.15s',
              }}>
                {tab.icon} {tab.label}
              </button>
            ))}
          </div>

          <div style={{ padding: '24px 28px' }}>

            {/* ── Edit Profile Tab ── */}
            {activeTab === 'edit' && (
              <form onSubmit={handleSaveProfile}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
                  <div>
                    <label style={labelSt}>Full Name *</label>
                    <input className="form-control" value={editForm.name}
                      onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="Your full name"
                    />
                  </div>
                  <div>
                    <label style={labelSt}>Department</label>
                    <input className="form-control" value={editForm.department}
                      onChange={e => setEditForm(f => ({ ...f, department: e.target.value }))}
                      placeholder="e.g. IT Support"
                    />
                  </div>
                  <div>
                    <label style={labelSt}>Phone Number</label>
                    <input className="form-control" value={editForm.phone}
                      onChange={e => setEditForm(f => ({ ...f, phone: e.target.value }))}
                      placeholder="e.g. +1 555 1234"
                    />
                  </div>
                  <div>
                    <label style={labelSt}>Email Address</label>
                    <input className="form-control" value={profile?.email || ''} disabled
                      style={{ opacity: 0.6, cursor: 'not-allowed' }}
                    />
                    <p style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                      Contact admin to change your email.
                    </p>
                  </div>
                </div>

                {editMsg.text && (
                  <div style={{
                    marginTop: 14, padding: '9px 14px', borderRadius: 8, fontSize: 13,
                    background: editMsg.type === 'success' ? 'rgba(16,185,129,0.10)' : 'rgba(239,68,68,0.10)',
                    color:      editMsg.type === 'success' ? '#059669'               : '#dc2626',
                    border: `1px solid ${editMsg.type === 'success' ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.25)'}`,
                  }}>
                    {editMsg.type === 'success' ? '✅' : '⚠️'} {editMsg.text}
                  </div>
                )}

                <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
                  <button type="submit" className="btn btn-primary" disabled={editSaving}
                    style={{ minWidth: 130, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                    {editSaving
                      ? <><span style={spinnerSt}/> Saving…</>
                      : '💾 Save Profile'
                    }
                  </button>
                </div>
              </form>
            )}

            {/* ── Change Password Tab ── */}
            {activeTab === 'security' && (
              <form onSubmit={handleChangePassword} style={{ maxWidth: 420 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {[
                    { key: 'current_password', label: 'Current Password',  placeholder: 'Enter current password' },
                    { key: 'new_password',      label: 'New Password',      placeholder: 'Min 6 characters'       },
                    { key: 'confirm',           label: 'Confirm Password',  placeholder: 'Re-enter new password'  },
                  ].map(f => (
                    <div key={f.key}>
                      <label style={labelSt}>{f.label}</label>
                      <div style={{ position: 'relative' }}>
                        <input
                          type={showPwd[f.key] ? 'text' : 'password'}
                          className="form-control"
                          value={pwdForm[f.key]}
                          onChange={e => setPwdForm(p => ({ ...p, [f.key]: e.target.value }))}
                          placeholder={f.placeholder}
                          style={{ paddingRight: 40 }}
                        />
                        <button type="button" onClick={() => setShowPwd(p => ({ ...p, [f.key]: !p[f.key] }))}
                          style={{
                            position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: 'var(--text-muted)', fontSize: 14, padding: 4,
                          }}>
                          {showPwd[f.key] ? '🙈' : '👁️'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Password strength indicator */}
                {pwdForm.new_password && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
                      {['weak', 'fair', 'good', 'strong'].map((level, i) => {
                        const len = pwdForm.new_password.length;
                        const hasUpper = /[A-Z]/.test(pwdForm.new_password);
                        const hasNum   = /\d/.test(pwdForm.new_password);
                        const hasSpec  = /[^a-zA-Z0-9]/.test(pwdForm.new_password);
                        const score = (len >= 8 ? 1 : 0) + (len >= 12 ? 1 : 0) + (hasUpper ? 1 : 0) + (hasNum ? 1 : 0) + (hasSpec ? 1 : 0);
                        const filled = score > i;
                        const colors = ['#ef4444', '#f97316', '#f59e0b', '#10b981'];
                        return (
                          <div key={level} style={{
                            flex: 1, height: 4, borderRadius: 2,
                            background: filled ? colors[Math.min(score - 1, 3)] : 'var(--border)',
                            transition: 'background 0.2s',
                          }} />
                        );
                      })}
                    </div>
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      Use uppercase, numbers, and symbols for a stronger password.
                    </span>
                  </div>
                )}

                {pwdMsg.text && (
                  <div style={{
                    marginTop: 14, padding: '9px 14px', borderRadius: 8, fontSize: 13,
                    background: pwdMsg.type === 'success' ? 'rgba(16,185,129,0.10)' : 'rgba(239,68,68,0.10)',
                    color:      pwdMsg.type === 'success' ? '#059669'               : '#dc2626',
                    border: `1px solid ${pwdMsg.type === 'success' ? 'rgba(16,185,129,0.25)' : 'rgba(239,68,68,0.25)'}`,
                  }}>
                    {pwdMsg.type === 'success' ? '✅' : '⚠️'} {pwdMsg.text}
                  </div>
                )}

                <button type="submit" className="btn btn-primary" disabled={pwdSaving}
                  style={{ marginTop: 20, minWidth: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
                  {pwdSaving
                    ? <><span style={spinnerSt}/> Updating…</>
                    : '🔐 Change Password'
                  }
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* ── Profile picture viewer ── */}
      {viewerOpen && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setViewerOpen(false); }}
          style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.78)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 22, padding: 20 }}
        >
          {/* Close (just dismiss the viewer) */}
          <button type="button" onClick={() => setViewerOpen(false)} title="Close"
            style={{ position: 'absolute', top: 18, right: 22, width: 40, height: 40, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.15)', color: '#fff', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <X size={22} />
          </button>

          {/* Full image */}
          <img
            src={profile?.avatar_url}
            alt={profile?.name || 'Profile'}
            style={{ maxWidth: 'min(80vw, 460px)', maxHeight: '64vh', borderRadius: 16, objectFit: 'contain', background: '#fff', boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
          />

          {/* Actions */}
          <div style={{ display: 'flex', gap: 14 }}>
            <button type="button" onClick={() => avatarInputRef.current?.click()} disabled={avatarUploading}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 22px', borderRadius: 10, border: 'none', background: 'var(--primary)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              <Upload size={16} /> {avatarUploading ? 'Working…' : 'Change Photo'}
            </button>
            <button type="button" onClick={handleAvatarDelete} disabled={avatarUploading}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 22px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.3)', background: 'rgba(220,38,38,0.9)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
              <Trash2 size={16} /> Remove Photo
            </button>
          </div>
        </div>
      )}
    </AppLayout>
  );
}

const labelSt = {
  display: 'block', fontSize: 11.5, fontWeight: 700,
  color: 'var(--text-muted)', letterSpacing: '0.6px',
  textTransform: 'uppercase', marginBottom: 6,
};

const spinnerSt = {
  display: 'inline-block', width: 14, height: 14,
  border: '2px solid rgba(255,255,255,0.35)',
  borderTopColor: '#fff', borderRadius: '50%',
  animation: 'spin 0.7s linear infinite',
};
